import { throwError } from '@daymade/common-lib/errors';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { MutableRefObject, useEffect, useRef, useState } from 'react';

// Taken from https://triphuntersworkspace.slack.com/archives/C061U2HAHDK/p1749490912785479?thread_ts=1749485774.213719&cid=C061U2HAHDK
const screenWidths = [
  390, 393, 360, 414, 412, 375, 430, 384, 428, 320, 1366, 1280, 1920, 768, 1440,
];

/** A page to help with viewing the same page across different viewport sizes
 *
 * Example usage - /responsive-preview?url=/about-us-2
 *
 */
const ResponsivePreview = () => {
  const router = useRouter();
  const { url, test, testGroups } = router.query; // Retrieves ?myParam=value
  const path =
    typeof url == 'string' && new URL(url, window.location.origin).pathname;
  const [heights, setHeights] = useState({} as Record<number, number>);
  const [widthVisibilities, setWidthVisibilities] = useState(
    {} as Record<number, boolean>,
  );
  const refsByWidth = useRef(
    {} as Record<number, MutableRefObject<HTMLIFrameElement | undefined>>,
  );
  const testVariantQueryStrings =
    test && testGroups
      ? testGroups
          .toString()
          .split(',')
          .map((x) => ({ name: x, value: '?test=' + test + '&testGroup=' + x }))
      : [{ name: '', value: '' }];

  for (const width of screenWidths) {
    refsByWidth.current[width] = useRef<HTMLIFrameElement>();
  }

  useEffect(() => {
    let ticker: ReturnType<typeof setTimeout> | undefined = undefined;
    const updateHeightsAndQueueTimeout = () => {
      ticker = setTimeout(() => {
        // Store the current height so that setting the height of iframes to zero doesn't affect the scroll position
        const iframeContainer =
          document.getElementById('iframesContainer') ??
          throwError('Expected element with ID iframeContainer');
        iframeContainer.style.minHeight = iframeContainer.scrollHeight + 'px';

        const newHeights: Record<number, number> = {};
        for (const width of screenWidths) {
          const refForCurrentWidth = refsByWidth.current[width]?.current;
          if (refForCurrentWidth) {
            const iframeDocument = refForCurrentWidth.contentDocument;
            if (iframeDocument?.body) {
              // Inject a stylesheet that overrides min-height: 100vh any elements
              // in the page. Without this, the wrapper expands to match
              // the iframe height each time we raise it, causing infinite growth.
              let stylesheet = iframeDocument.documentElement.querySelector(
                '#ResponsivePreviewStylesheet',
              );
              if (!stylesheet) {
                stylesheet = iframeDocument.createElement('style');
                stylesheet.setAttribute('id', 'ResponsivePreviewStylesheet');
                iframeDocument.head.appendChild(stylesheet);
              }
              stylesheet.innerHTML =
                '* { min-height: 0 !important; min-block-size: 0 !important; }';

              // Also collapse the iframe viewport so any remaining 100vh
              // references resolve to 0 during the scrollHeight read. This is
              // not perfect, elements within the iframes that are 100vh will be
              // 100% of the height of the iframe, possibly causing
              // excess whitespace below them
              refForCurrentWidth.style.height = '0px';
              newHeights[width] = iframeDocument.documentElement.scrollHeight;

              // Reset the styles back to how they were before measuring
              refForCurrentWidth.style.height = '';
              stylesheet.innerHTML = '';
            }
          }
        }

        // Restore the original height to prevent scroll position reset.
        document.body.style.minHeight = '';

        setHeights(newHeights);
        updateHeightsAndQueueTimeout();
      }, 1000);
    };
    updateHeightsAndQueueTimeout();

    return () => {
      if (ticker) {
        clearTimeout(ticker);
      }
    };
  }, []);

  return (
    <div className="pageContainer">
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <header>
        Select the screen widths to display. You can also pass the query string
        parameters <strong>test</strong> and <strong>testGroups</strong> (as a
        CSV) to show test variants.
        <br />
        Screen width:{' '}
        {screenWidths.map((x) => (
          <span className="checkboxContainer">
            <input
              id={'checkbox' + x}
              type="checkbox"
              checked={widthVisibilities[x] || false}
              onChange={(e) => {
                setWidthVisibilities((prev) => {
                  const copy = { ...prev };
                  if (e.target.checked) {
                    copy[x] = true;
                  } else {
                    delete copy[x];
                  }
                  return copy;
                });
              }}
            />{' '}
            <label htmlFor={'checkbox' + x}>{x}</label>{' '}
          </span>
        ))}
      </header>
      <div className="iframeScroller">
        <div id="iframesContainer" className="iframesContainer">
          {path &&
            screenWidths.map(
              (x) =>
                widthVisibilities[x] &&
                testVariantQueryStrings.map((y) => (
                  <div key={x + '_' + y} className="iframeContainer">
                    {x}
                    {y.name ? ' - ' + y.name : ''}
                    <iframe
                      ref={
                        refsByWidth.current[
                          x
                        ] as MutableRefObject<HTMLIFrameElement | null>
                      }
                      scrolling="no"
                      width={x}
                      height={heights[x] || 600}
                      src={path + y.value}
                    />
                  </div>
                )),
            )}
        </div>
      </div>

      <style>
        {`
          #__next { height: 100%; }

          .pageContainer {
            display: flex;
            flex-direction: column;
            height: 100%
          }
          header {
            margin-left: 10px;
            margin-right: 10px;
          }

          .iframeScroller {
            overflow-x: scroll;
            flex: 1;
          }
          iframe {
            display: block;
            box-shadow: rgba(0,0,0,0.3) 0px 0px 12px;
            overflow: hidden;
            border: 0;
          }
            .iframesContainer {
              display: flex;
              height: 100%;
            }
          .iframeContainer {
            display: inline-block;
            margin: 10px;
          }
          label {
            padding-right: 6px;
            vertical-align: middle;
          }
          input[type=checkbox] {
            vertical-align: middle;
          }
          .checkboxContainer {
            display: inline-block;
          }
          strong {
            font-size: inherit;
            line-height: inherit;
          }

        `}
      </style>
    </div>
  );
};

export default ResponsivePreview;
